import { useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { useAction, useQuery } from '@/lib/data'
import { useAuth, useCan } from '@/auth/useAuth'
import { SPACE_ROLE_LABELS, type SpaceRole } from '@/auth/permissions'
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Modal,
  Select,
  Skeleton,
  Table,
  Td,
  Th,
} from '@/components/ui'

/**
 * Mitgliederverwaltung eines Bereichs.
 *
 * Bis hierher ging das nur über SQL. Das war kein Zwischenstand, sondern eine
 * Lücke: die Bereichszugehörigkeit entscheidet, was jemand in den Fachmodulen
 * überhaupt sieht — wer sie nicht ohne Datenbankzugang setzen kann, kann das
 * System nicht in Betrieb nehmen.
 *
 * Geschrieben wird ausschliesslich über die RPCs `set_space_role` und
 * `remove_space_member`. Beide sind `security definer`, prüfen das Recht und
 * schreiben den Audit-Eintrag in derselben Transaktion. Ein direktes INSERT
 * auf `space_members` aus dem Client würde zwar an der RLS scheitern — aber
 * eben ohne Protokolleintrag, und darauf will man sich nicht verlassen.
 *
 * Die Liste selbst sieht jedes Bereichsmitglied. Die Bedienelemente zum Ändern
 * bekommt nur, wer `space:manage_members` hat. Das ist Komfort, keine
 * Sicherheitsgrenze — die liegt in den RPCs.
 */

const SPACE_ROLES: SpaceRole[] = [
  'space_lead',
  'space_editor',
  'space_contributor',
  'space_viewer',
]

interface MemberRow {
  user_id: string
  role_key: SpaceRole
  granted_at: string
}

interface PersonRow {
  id: string
  full_name: string | null
  email: string
  department: string | null
  status: 'active' | 'suspended'
}

function displayName(person: PersonRow | undefined, fallback: string): string {
  return person?.full_name || person?.email || fallback
}

export function SpaceMembers({ spaceId }: { spaceId: string }) {
  const { profile } = useAuth()
  const canManage = useCan('space:manage_members', spaceId)
  // Die eigene Bereichsrolle darf nur ändern, wer Rollen global vergeben darf.
  // Ohne diese Sperre könnte sich eine Bereichsleitung nicht versehentlich,
  // aber sehr wohl absichtlich selbst hochstufen.
  const canChangeOwn = useCan('role:assign')

  const [addOpen, setAddOpen] = useState(false)
  const [pick, setPick] = useState('')
  const [pickRole, setPickRole] = useState<SpaceRole>('space_viewer')

  const members = useQuery<MemberRow[]>(`members:${spaceId}`, () =>
    supabase
      .from('space_members')
      .select('user_id, role_key, granted_at')
      .eq('space_id', spaceId)
      .order('granted_at'),
  )

  // Personen getrennt laden statt eingebettet: `space_members.user_id` zeigt
  // auf auth.users, nicht auf profiles — PostgREST kann darüber nicht
  // einbetten. Zwei kurze Abfragen sind hier ehrlicher als eine Sicht, die
  // nur dafür existiert.
  const people = useQuery<PersonRow[]>('people', () =>
    supabase
      .from('profiles')
      .select('id, full_name, email, department, status')
      .order('full_name', { nullsFirst: false }),
  )

  const byId = useMemo(() => {
    const map = new Map<string, PersonRow>()
    for (const person of people.data ?? []) map.set(person.id, person)
    return map
  }, [people.data])

  const memberIds = useMemo(
    () => new Set((members.data ?? []).map((m) => m.user_id)),
    [members.data],
  )

  const candidates = (people.data ?? []).filter(
    (person) => !memberIds.has(person.id) && person.status === 'active',
  )

  const action = useAction()

  async function setRole(userId: string, role: SpaceRole) {
    const ok = await action.run(() =>
      supabase.rpc('set_space_role', { p_space: spaceId, p_user: userId, p_role: role }),
    )
    if (ok) members.reload()
  }

  async function remove(userId: string) {
    const ok = await action.run(() =>
      supabase.rpc('remove_space_member', { p_space: spaceId, p_user: userId }),
    )
    if (ok) members.reload()
  }

  async function add() {
    if (!pick) return
    const ok = await action.run(() =>
      supabase.rpc('set_space_role', { p_space: spaceId, p_user: pick, p_role: pickRole }),
    )
    if (ok) {
      setAddOpen(false)
      setPick('')
      setPickRole('space_viewer')
      members.reload()
    }
  }

  const rows = members.data ?? []

  return (
    <Card
      title="Mitglieder"
      flush={rows.length > 0}
      actions={
        canManage ? (
          <Button size="sm" onClick={() => setAddOpen(true)} disabled={candidates.length === 0}>
            Person hinzufügen
          </Button>
        ) : undefined
      }
    >
      {action.error && (
        <Alert tone="danger" title="Änderung nicht möglich">
          {action.error}
        </Alert>
      )}

      {members.error && (
        <Alert tone="danger" title="Mitglieder konnten nicht geladen werden">
          {members.error}
        </Alert>
      )}

      {members.loading ? (
        <Skeleton height="6em" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="Noch niemand zugeordnet"
          description="Solange niemand Mitglied ist, bleiben die Fachmodule dieses Bereichs für alle leer — das ist kein Fehler, sondern die Regel, nach der die Datenbank filtert."
        />
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Person</Th>
              <Th>Abteilung</Th>
              <Th>Rolle</Th>
              {canManage && <Th>Aktion</Th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const person = byId.get(row.user_id)
              const self = row.user_id === profile?.id
              const locked = self && !canChangeOwn

              return (
                <tr key={row.user_id}>
                  <Td>
                    <span className="member-cell">
                      <Avatar name={displayName(person, row.user_id)} size="sm" />
                      <span className="member-cell__text">
                        <span>{displayName(person, 'Unbekannt')}</span>
                        <span className="numeric text-tertiary">{person?.email ?? '—'}</span>
                      </span>
                    </span>
                  </Td>
                  <Td>{person?.department ?? '—'}</Td>
                  <Td>
                    {canManage && !locked ? (
                      <Select
                        aria-label={`Rolle von ${displayName(person, 'dieser Person')}`}
                        value={row.role_key}
                        disabled={action.busy}
                        onChange={(event) =>
                          setRole(row.user_id, event.target.value as SpaceRole)
                        }
                      >
                        {SPACE_ROLES.map((role) => (
                          <option key={role} value={role}>
                            {SPACE_ROLE_LABELS[role]}
                          </option>
                        ))}
                      </Select>
                    ) : (
                      <Badge tone={self ? 'accent' : 'neutral'} outline={!self}>
                        {SPACE_ROLE_LABELS[row.role_key]}
                      </Badge>
                    )}
                  </Td>
                  {canManage && (
                    <Td>
                      <Button
                        size="sm"
                        variant="danger"
                        disabled={action.busy || locked}
                        onClick={() => remove(row.user_id)}
                      >
                        Entfernen
                      </Button>
                    </Td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}

      {canManage && !canChangeOwn && memberIds.has(profile?.id ?? '') && (
        <p className="text-tertiary form-note">
          Die eigene Bereichsrolle lässt sich hier nicht ändern. Dafür wird das
          Recht <code>role:assign</code> benötigt — sonst könnte sich eine
          Bereichsleitung selbst hochstufen.
        </p>
      )}

      <Modal
        open={addOpen}
        title="Person hinzufügen"
        description="Die Person erhält Zugriff auf die Inhalte dieses Bereichs, im Umfang der gewählten Rolle."
        onClose={() => setAddOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={add} loading={action.busy} disabled={!pick}>
              Hinzufügen
            </Button>
          </>
        }
      >
        <div className="stack-md">
          <FormField label="Person" required>
            {(props) => (
              <Select
                {...props}
                value={pick}
                onChange={(event) => setPick(event.target.value)}
              >
                <option value="">Bitte wählen …</option>
                {candidates.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.full_name ? `${person.full_name} — ${person.email}` : person.email}
                  </option>
                ))}
              </Select>
            )}
          </FormField>

          <FormField
            label="Rolle"
            hint="Leser darf lesen, Mitwirkender anlegen, Redakteur freigeben, Bereichsleitung zusätzlich Mitglieder verwalten."
            required
          >
            {(props) => (
              <Select
                {...props}
                value={pickRole}
                onChange={(event) => setPickRole(event.target.value as SpaceRole)}
              >
                {SPACE_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {SPACE_ROLE_LABELS[role]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        </div>
      </Modal>
    </Card>
  )
}
